import * as React from 'react';

import { useFunctionInBulk } from '@notino/react-web-utils';
import {
  WishlistForCurrentUserQuery,
  WishlistSortBy,
} from '@notino/shared/definitions/types';
import { Ga4Events, Product } from '@notino/web-tracking';

import { GTM_WISHLIST_LIST_NAME } from '@containers/WishList/constants';
import { useWishlistStore } from '@containers/WishList/store';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { ProductEventWither } from '@helpers/googleTagManager';

type NonArray<T> = T extends Array<unknown> ? never : T;
type Ga4SortingType = NonArray<
  Extract<Ga4Events, { event: 'view_item_list' }>['product_lists']
>['sorting_type'];

type TrackingWishlistProduct = Product & {
  id: string;
  list_position: number;
};

type Params = { sortBy: WishlistSortBy };

const sortMapping: Record<WishlistSortBy, Ga4SortingType> = {
  Default: 'recommendation',
  Cheapest: 'price_ascending',
  MostExpensive: 'price_descending',
  BestSellers: 'bestsellers',
  TopRated: 'topRated',
  Alphabetically: 'alphabetical',
};

export const useWishlistViewItemListTracking = () => {
  const storeProducts = useWishlistStore((state) => state.products);
  const sortBy = useWishlistStore((state) => state.selectedSortBy);

  const trackProductsImpression = React.useCallback(
    (products: TrackingWishlistProduct[], { sortBy: _sortBy }: Params) => {
      dispatchTrackingEvent({
        event: 'view_item_list',
        product_lists: {
          products,
          interaction: window.scrollY > 100 ? 'scroll' : 'automatic',
          sorting_type: sortMapping[_sortBy],
          list_id: undefined,
          list_name: GTM_WISHLIST_LIST_NAME,
        },
        _clear: true,
      });
    },
    []
  );

  const trackProductsImpressionBulk = useFunctionInBulk<
    TrackingWishlistProduct,
    // @ts-ignore
    Params
  >(trackProductsImpression, 3000, { sortBy });

  return (
    product: NonNullable<
      NonNullable<
        WishlistForCurrentUserQuery['WishlistForCurrentUser']
      >['listing']['products'][number]['product']
    >
  ) => {
    if (!product) return;
    trackProductsImpressionBulk(
      ProductEventWither()
        .withVpProduct(product as any)
        .withDiscountPrices(product.attributes, {
          shouldSendOnlyDiscountPrice: true,
        })
        .withAdditionalData({
          id: product.id,
          list_position:
            storeProducts.findIndex((p) => String(p?.id) === product.id) + 1,
        })
        .build() as TrackingWishlistProduct
    );
  };
};

export type TrackedProduct = Product & { list_position: number; id: string };
